<template>
  <div>
    <button
      type="button"
      class="btn btn-secondary btn-outlined --small rounded font-medium input-normal mdsm:input-tall itinerary-detail-button mdsm:btn-outlined"
      @click="onClick"
    >
      <span class="lg:hidden">Trip details</span>
      <span class="mdsm:hidden">View trip details</span>
    </button>

    <zb-modal
      ref="detailModal"
      class="itinerary-details-modal slide-in-modal"
      :no-padding="true"
    >
      <template v-slot:default>
        <div>
          <div class="px-6">
            <div class="px-5">
              <h4 class="fs-20 font-medium mb-5 sm:mb-4">Overview</h4>
              <div class="flex mb-4 sm:display-contents">
                <div class="flex-1 w-1/2 sm:w-full sm:flex-none sm:mb-4">
                  <label class="text-black-light-5 block">Length of trip</label>
                  {{ trip.itineraries.itinerary_length + 1 }} days /
                  {{ trip.itineraries.itinerary_length }} nights
                </div>
                <div class="flex-1 w-1/2 sm:w-full sm:flex-none sm:mb-4">
                  <label class="text-black-light-5 block"
                    >Number of dives</label
                  >
                  {{ trip.itineraries.dive_number }}
                </div>
              </div>
              <div class="flex mb-4 sm:display-contents">
                <div class="flex-1 w-1/2 sm:w-full sm:flex-none sm:mb-4">
                  <label class="text-black-light-5 block">Experience</label>
                  {{ trip.itineraries.experience }}
                </div>
                <div class="flex-1 w-1/2 sm:w-full sm:flex-none sm:mb-4">
                  <label class="text-black-light-5 block">Insurance</label>
                  {{ trip.itineraries.dive_insurance }}
                </div>
              </div>
            </div>

            <hr class="my-8 border border-black-30" />

            <div class="px-5">
              <h4 class="fs-20 font-medium mb-5">Transfer details</h4>
              <div class="flex sm:display-contents">
                <div class="flex-1 w-1/2 sm:w-full">
                  <h5 class="mb-4 font-medium">Departure</h5>
                  <p class="mb-2 flex items-center">
                    <svg width="24" height="24" class="inline-block mr-2">
                      <use xlink:href="/img/sprite.svg#ic_checkin"></use>
                    </svg>
                    {{ trip.departure_date | formatDate("DD MMM YYYY") }}
                  </p>
                  <p class="mb-2 flex items-center">
                    <svg width="24" height="24" class="inline-block mr-2">
                      <use xlink:href="/img/sprite.svg#ic_clock"></use>
                    </svg>
                    {{ trip.itineraries.departure_time | formatTime }}
                  </p>
                  <p class="mb-2 flex items-center">
                    <svg width="24" height="24" class="inline-block mr-2">
                      <use xlink:href="/img/sprite.svg#ic_anker"></use>
                    </svg>
                    {{ trip.itineraries.departure_port }}
                  </p>
                  <p class="mb-2 flex items-center">
                    <svg width="24" height="24" class="inline-block mr-2">
                      <use xlink:href="/img/sprite.svg#ic_plane"></use>
                    </svg>
                    {{ trip.itineraries.departure_airport }}
                  </p>
                </div>
                <div class="flex-1 w-1/2 sm:w-full">
                  <h5 class="mb-4 font-medium sm:mt-6">Return</h5>
                  <p class="mb-2 flex items-center">
                    <svg width="24" height="24" class="inline-block mr-2">
                      <use xlink:href="/img/sprite.svg#ic_checkout"></use>
                    </svg>
                    {{ returnDate }}
                  </p>
                  <p class="mb-2 flex items-center">
                    <svg width="24" height="24" class="inline-block mr-2">
                      <use xlink:href="/img/sprite.svg#ic_clock"></use>
                    </svg>
                    {{ trip.itineraries.return_time | formatTime }}
                  </p>
                  <p class="mb-2 flex items-center">
                    <svg width="24" height="24" class="inline-block mr-2">
                      <use xlink:href="/img/sprite.svg#ic_anker"></use>
                    </svg>
                    {{ trip.itineraries.return_port }}
                  </p>
                  <p class="mb-2 flex items-center">
                    <svg width="24" height="24" class="inline-block mr-2">
                      <use xlink:href="/img/sprite.svg#ic_plane"></use>
                    </svg>
                    {{ trip.itineraries.return_airport }}
                  </p>
                </div>
              </div>
            </div>

            <accordion class="mt-6">
              <accordion-item
                v-for="(item, index) in tripInfo"
                :key="index"
                v-if="item.children.some((o) => !!o.value)"
              >
                <template #accordion-trigger="{ active }">
                  <h4
                    class="fs-20 font-medium mb-5 pt-6 flex items-center justify-between border-t border-black-30 pl-5"
                  >
                    {{ item.label }}
                    <span class="mr-4">
                      <svg width="24" height="24" v-if="active">
                        <use xlink:href="/img/sprite.svg#ic_chevron-up"></use>
                      </svg>
                      <svg width="24" height="24" v-else>
                        <use xlink:href="/img/sprite.svg#ic_chevron-down"></use>
                      </svg>
                    </span>
                  </h4>
                </template>
                <template #accordion-content>
                  <div
                    v-for="(content, i) in item.children"
                    :key="i"
                    class="pb-6 px-5"
                  >
                    <div v-if="content.label" class="font-medium mb-2">
                      {{ content.label }}
                    </div>
                    <div v-html="content.value" class="standard-list"></div>
                  </div>
                </template>
              </accordion-item>
            </accordion>

            <hr class="mb-8 mt-6 border border-black-30" />

            <div class="px-5 mb-6">
              <h4 class="fs-20 font-medium mb-5">Highlights</h4>
              <div
                class="standard-list"
                v-html="trip.itineraries.itinerary_bullet"
              ></div>
            </div>

            <accordion>
              <accordion-item v-if="destinationData">
                <template #accordion-trigger="{ active }">
                  <h4
                    class="fs-20 font-medium mb-5 pt-6 flex items-center justify-between border-t border-black-30 pl-5"
                  >
                    Diving environments
                    <span class="mr-4">
                      <svg width="24" height="24" v-if="active">
                        <use xlink:href="/img/sprite.svg#ic_chevron-up"></use>
                      </svg>
                      <svg width="24" height="24" v-else>
                        <use xlink:href="/img/sprite.svg#ic_chevron-down"></use>
                      </svg>
                    </span>
                  </h4>
                </template>
                <template #accordion-content>
                  <div class="px-5">
                    <species-visibility
                      :destination-data="destinationData"
                      :selected-time="selectedTime"
                    />

                    <div class="mt-8">
                      <a
                        v-for="(info, sku) in destinationData"
                        :key="sku"
                        :href="info.url"
                        target="_blank"
                        class="inline-block mb-4 mr-4 sm:m-0"
                      >
                        <button
                          type="button"
                          class="btn btn-outlined btn-secondary rounded font-medium input-tall sm:w-full sm:mb-4"
                        >
                          Explore {{ info.title }}
                        </button>
                      </a>
                    </div>
                  </div>
                </template>
              </accordion-item>

              <accordion-item
                v-for="(item, index) in tripAccordion"
                :key="index"
                v-if="item.children.some((o) => !!o.value)"
              >
                <template #accordion-trigger="{ active }">
                  <h4
                    class="fs-20 font-medium mb-5 pt-6 flex items-center justify-between border-t border-black-30 pl-5"
                  >
                    {{ item.label }}
                    <span class="mr-4">
                      <svg width="24" height="24" v-if="active">
                        <use xlink:href="/img/sprite.svg#ic_chevron-up"></use>
                      </svg>
                      <svg width="24" height="24" v-else>
                        <use xlink:href="/img/sprite.svg#ic_chevron-down"></use>
                      </svg>
                    </span>
                  </h4>
                </template>
                <template #accordion-content>
                  <div
                    v-for="(content, i) in item.children"
                    :key="i"
                    class="pb-6 px-5"
                  >
                    <div v-if="content.label" class="font-medium mb-2">
                      {{ content.label }}
                    </div>
                    <div v-html="content.value" class="standard-list"></div>
                  </div>
                </template>
              </accordion-item>
            </accordion>

            <div class="mdsm:visible lg:hidden mt-16 mb-8">
              <a :href="buildItineraryURL()" target="_blank">
                <button
                  type="button"
                  class="btn btn-secondary btn-outlined font-medium input-tall w-full"
                >
                  View itinerary in new tab
                </button>
              </a>
            </div>
          </div>
        </div>
      </template>

      <template #stickyFooter>
        <footer
          class="p-6 text-right sm:text-left border-t border-black-30"
          v-if="lowestPrice"
        >
          <div class="flex items-center">
            <div class="mdsm:hidden lg:visible">
              <a :href="buildItineraryURL()" target="_blank">
                <button
                  type="button"
                  class="btn btn-secondary btn-outlined font-medium input-tall"
                >
                  View itinerary in new tab
                </button>
              </a>
            </div>
            <div class="flex-1 mr-4">
              from
              <currency-context>
                <template v-slot="{ convert }">
                  <span
                    class="fs-20 font-medium"
                    v-text="convert(lowestPrice.final_price)"
                  ></span>
                </template>
              </currency-context>
              /guest
              <p class="fs-14 text-black-light-5 -mt-1">
                Per person, diving included
              </p>
            </div>

            <div class="flex-none">
              <button
                type="button"
                class="btn btn-primary rounded font-medium input-tall"
                @click="handleEnquireClicked"
              >
                Select cabin
              </button>
            </div>
          </div>
        </footer>
      </template>
    </zb-modal>
  </div>
</template>

<script>
import get from "lodash/get";
import ZbModal from "../../components/core/ZbModal";
import { Accordion, AccordionItem } from "../../components/Accordion";
import SpeciesVisibility from "../../views/liveaboard/SpeciesVisibility";
import CurrencyContext from "../../components/core/CurrencyContext";

const TRIP_DETAILS = [
  {
    label: "Important to know",
    children: [
      { key: "info_before_you_go", label: "Before you go" },
      { key: "info_during_your_trip", label: "During your trip" },
    ],
  },
  {
    label: "How to get there",
    children: [
      { key: "info_start_of_trip", label: "Start of the trip" },
      { key: "info_end_of_trip", label: "End of the trip" },
    ],
  },
];

const TRIP_INFO = [
  {
    label: "What's included",
    children: [{ key: "info_what_is_included" }],
  },
  {
    label: "What's not included",
    children: [
      {
        key: "info_what_is_not_included",
        label: "To be paid in advance or onboard",
      },
    ],
  },
  {
    label: "Optional Extras",
    children: [
      {
        key: "info_optional_extras",
        label: "To be booked in advance or paid onboard",
      },
    ],
  },
];

export default {
  components: {
    ZbModal,
    SpeciesVisibility,
    Accordion,
    AccordionItem,
    CurrencyContext,
  },
  props: ["trip", "selectedTime", "supplier"],
  filters: {
    formatDate(timeString, format) {
      return moment(timeString).format(format);
    },
    formatTime(timeString) {
      return moment(timeString, "HH:mm:ss").format("hh:mma");
    },
  },
  data() {
    return {
      destinationData: null,
    };
  },
  computed: {
    titleButton() {
      const isMobile = window.matchMedia(
        "only screen and (max-width: 1200px)"
      ).matches;
    },
    lowestPrice() {
      return this.trip.prices.find((price) => price.lowest_price);
    },
    destinationLinks() {
      let urls = [];

      for (const destinationSKU in this.destinationData) {
        urls.push(this.destinationData[destinationSKU].url);
      }

      return urls;
    },
    returnDate() {
      const startDate = moment(this.trip.departure_date);
      const end = startDate.add(this.trip.itineraries.itinerary_length, "days");

      return end.format("DD MMM YYYY");
    },
    tripAccordion() {
      const result = TRIP_DETAILS.map((item) => {
        return {
          ...item,
          children: item.children.map((val) => ({
            ...val,
            value: get(this.trip, ["itineraries", val.key]),
          })),
        };
      });

      // Check for payment & cancellation
      const extraItem = [];
      if (this.supplier.payment_policy) {
        extraItem.push({
          value: this.supplier.payment_policy,
          label: "Payment policy",
        });
      }
      if (this.supplier.cancelation_policy) {
        extraItem.push({
          value: this.supplier.cancelation_policy,
          label: "Cancellation policy",
        });
      }

      if (extraItem.length > 0) {
        result.push({
          label: "Payment & cancellation terms",
          children: extraItem,
        });
      }

      return result;
    },
    tripInfo() {
      const result = TRIP_INFO.map((item) => {
        return {
          ...item,
          children: item.children.map((val) => ({
            ...val,
            value: get(this.trip, ["itineraries", val.key]),
          })),
        };
      });

      return result;
    },
  },
  mounted() {
    if (this.trip.available === "Sold out") {
      return;
    }

    let destinations = "";

    try {
      destinations = JSON.parse(this.trip.itineraries.destination).join(",");
    } catch (error) {}

    if (!destinations) {
      return;
    }

    $.ajax({
      method: "POST",
      url: "/",
      data: {
        destination_ids: destinations,
        action: "zuBlu/public/getDestinationsDivingEnvironment",
        [window.Craft.csrfTokenName]: window.Craft.csrfTokenValue,
      },
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    }).done((response) => {
      try {
        this.destinationData = JSON.parse(response);
      } catch (error) {}
    });
  },
  methods: {
    slugify(anytext) {
      let slugified = String(anytext || "")
        .toLowerCase()
        .replace(/[^A-Za-z0-9]/g, "-");
      slugified = slugified.replace(/---/g, "-");
      slugified = slugified.replace(/--/g, "-");

      return slugified;
    },
    buildItineraryURL() {
      const supplierName = this.supplier.name;
      const itineraryName = this.trip.itineraries.name.toLowerCase();
      const itineraryCode = this.trip.itineraries.code.toLowerCase();

      const slugifiedItineraryName = this.slugify(itineraryName);
      const slugifiedSupplierName = this.slugify(supplierName);

      // eg: /itineraries/coralia/itn-2010171155151-komodo
      return `/itineraries/${slugifiedSupplierName}/${itineraryCode}-${slugifiedItineraryName}`;
    },
    onClick() {
      this.$refs.detailModal.show();

      this.$el.querySelector(".zb-modal-scrollable-content").scrollTop = 0;
    },
    handleEnquireClicked() {
      this.$refs.detailModal.hide();

      this.$nextTick(() => {
        this.$emit("enquire");
      });
    },
  },
};
</script>

<style>
.itinerary-details-modal .zb-modal-content {
  width: 780px;
}

@media (min-width: 601px) and (max-width: 1200px) {
  .itinerary-details-modal .zb-modal-content {
    max-width: 100%;
    width: auto;
    margin-left: 40px;
    margin-right: 40px;
  }
}
</style>

<style scoped>
.standard-list ::v-deep ul {
  margin-top: 1rem;
  margin-bottom: 1.5rem;
}

::v-deep .standard-list div > br:last-child {
  display: unset;
}

@media (max-width: 600px) {
  .itinerary-detail-button {
    height: 48px;
    width: 100%;
  }
}
</style>
